import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageSelectorModalCopy, VideoSelectorModal } from "./customModals";

import { InputForm, TextAreaForm, InputFile } from "../atoms/input";
import { HorizontalLayout, VerticalLayout } from "../components/layout";
import { StandardButton } from "../atoms/buttons";
import {
  IconPhoto,
  IconTrash,
  IconDeviceFloppy,
  IconX,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlus,
  IconArrowRight,
  IconEdit,
  IconVideo
} from "@tabler/icons-react";
import { postAPImedia, ip } from "../api/api";
import { tr } from "date-fns/locale";
import { position } from "polished";

function IconButton({ onClick, icon, className = "video-control-btn", title }) {
  return (
    <button className={className} onClick={onClick} title={title}>
      {icon}
    </button>
  );
}

function CloseButton({ onClick }) {
  return (
    <button onClick={onClick} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}>
      <IconX size={16} />
    </button>
  );
}

function VideoElement({ videoRef, src, onClick, playsInline }) {
  return (
    <video
      ref={videoRef}
      src={src}
      className="video-touchpoint-video"
      onClick={onClick}
      playsInline={playsInline}
    />
  );
}

function TouchpointHintPill({ visible, text }) {
  if (!visible) return null;
  return (
    <AnimatePresence>
      <motion.div
        className="touchpoint-hint-pill"
        initial={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0 }}
        animate={{ opacity: 1, width: "auto", paddingLeft: "1rem", paddingRight: "1rem" }}
        exit={{ opacity: 0, width: 0, paddingLeft: 0, paddingRight: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden", whiteSpace: "nowrap" }}
      >
        <b>Tip:</b> {text}
      </motion.div>
    </AnimatePresence>
  );
}

function SceneEditingIndicator({ title, onClose }) {
  return (
    <div className="scene-editing-indicator">
      <span>Bewerken: {title}</span>
      <button className="scene-close-btn" onClick={onClose}>
        <IconX size={16} />
      </button>
    </div>
  );
}

function VideoTimeline({ timelineRef, onClick, progress, children }) {
  return (
    <div ref={timelineRef} className="video-timeline" onClick={onClick}>
      <div className="video-progress" style={{ width: `${progress}%` }} />
      {children}
    </div>
  );
}

function SceneMarker({ active, position, onClick, title }) {
  return (
    <div
      className={`video-frame-marker ${active ? "active" : ""}`}
      style={{ left: `${position}%` }}
      onClick={onClick}
      title={title}
    />
  );
}

function VideoTimeDisplay({ current, total }) {
  return (
    <span className="video-time">
      {formatFrame(current)} / {formatFrame(total)}
    </span>
  );
}

function PlayPauseButton({ isPlaying, onClick }) {
  return (
    <IconButton
      onClick={onClick}
      icon={isPlaying ? <IconPlayerPause size={20} /> : <IconPlayerPlay size={20} />}
    />
  );
}

function SceneListItem({ scene, isEditing, isEditingTitle, titleInput, onTitleChange, onTitleBlur, onTitleKeyDown, onTitleClick, onEdit, onDelete }) {
  return (
    <div className={`frame-item ${isEditing ? "active" : ""}`}>
      <HorizontalLayout style={{ gap: "0.5rem", flex: 1 }}>
        {isEditingTitle ? (
          <input
            type="text"
            value={titleInput}
            onChange={onTitleChange}
            onBlur={onTitleBlur}
            onKeyDown={onTitleKeyDown}
            className="scene-title-input"
            autoFocus
          />
        ) : (
          <span className="scene-title" onClick={onTitleClick}>
            {scene.title}
          </span>
        )}
        <span className="scene-time">{formatFrame(getSceneFrame(scene))}</span>
        <span className="scene-touchpoints">{scene.touchpoints.length} touchpoints</span>
      </HorizontalLayout>
      <HorizontalLayout style={{ gap: "0.25rem", width: "auto" }}>
        <IconButton onClick={onEdit} icon={<IconEdit size={16} />} />
        <IconButton onClick={onDelete} icon={<IconTrash size={16} />} className="video-control-btn video-control-btn-danger" />
      </HorizontalLayout>
    </div>
  );
}

function TouchpointMarker({ position, isActive, onClick, children }) {
  const isMobile = window.innerWidth <= 768;
  return (
    <motion.div
      style={{
        top: position.y,
        left: position.x,
        cursor: "pointer",
        zIndex: isActive ? 2 : 1,
      }}
      className={isActive ? "interactive-tp-active" : "interactive-tp"}
      onClick={onClick}
      {...touchpointAnimation}
    >
      {children}
    </motion.div>
  );
}

function VideoPlayOverlay({ onClick }) {
  return (
    <div className="video-play-overlay" onClick={onClick}>
      <div className="video-play-button">
        <IconPlayerPlay size={48} />
      </div>
    </div>
  );
}

function ContinueButton({ onClick }) {
  return (
    <motion.div
      className="continue-button-container"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
    >
      <button className="continue-button" onClick={onClick}>
        Volgende <IconArrowRight size={18} />
      </button>
    </motion.div>
  );
}

function SceneTitleDisplay({ title }) {
  return (
    <motion.div
      className="scene-title-display"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {title}
    </motion.div>
  );
}

function InteractiveImage({ src, onClick, onContextMenu }) {
  return (
    <img
      className="interactive-bg-image"
      src={src}
      onClick={onClick}
      onContextMenu={onContextMenu}
      style={{ maxHeight: "80vh", width: "100%", height: "auto" }}
    />
  );
}

function ImageTouchpointWrapper({ position, isActive, children }) {
  return (
    <div
      className="tp-wrapper"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        zIndex: isActive ? 2 : 1,
        pointerEvents: "auto"
      }}
    >
      {children}
    </div>
  );
}

function getPopoverStyle(x, y, position) {
  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    return {
      position: "fixed",
      bottom: "0",
      left: "50%",
      zIndex: 1000000,
      width: "calc(100vw - 2rem)",
      transform: "translate(-50%, -50%)",
      maxWidth: "22rem",
      backgroundColor: "var(--primary-50)",
      border: "1px solid var(--border-color)",
      padding: "15px",
      borderRadius: "8px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    };
  }
  const isRightSide = parseFloat(position.x) >= 50;

  return {
    position: "absolute",
    top: y,
    zIndex: 1000000,
    backgroundColor: "var(--primary-50)",
    border: "1px solid var(--border-color)",
    padding: "15px",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    maxWidth: "20rem",
    minWidth: "15rem",
    transform: "translate(-50%, -50%)",
    ...(isRightSide ? { right: x } : { left: x }),
  };
}
const popoverPhoneAnimation = {
  initial: { opacity: 0, scale: 0.8, y: "-50%", x: "-50%", },
  animate: { opacity: 1, scale: 1, y: "-50%", x: "-50%" },
  exit: { opacity: 0, scale: 0.8, y: "-50%", x: "-50%" },
  transition: { duration: 0.2, ease: "easeOut" },
}

const popoverAnimation = {
  initial: { opacity: 0, scale: 0.8, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.8, y: 10 },
  transition: { duration: 0.2, ease: "easeOut" },
};

const touchpointAnimation = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0, opacity: 0 },
  transition: { duration: 0.3, ease: "backOut" },
};

function TouchpointPopover({ x, y, visible, onClose, readOnly, onUpdate, content, index, isNewTouchpoint, position, sceneIndex }) {
  const titleInputRef = useRef(null);
  const [localTitle, setLocalTitle] = useState("");
  const [localDescription, setLocalDescription] = useState("");

  const isVideoMode = sceneIndex !== undefined;
  const currentTP = isVideoMode
    ? content.scenes?.[sceneIndex]?.touchpoints?.[index]
    : content.touchpoints?.[index];

  useEffect(() => {
    if (visible && isNewTouchpoint && titleInputRef.current && !readOnly) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [visible, isNewTouchpoint, readOnly]);

  useEffect(() => {
    if (visible && currentTP) {
      setLocalTitle(currentTP.title || "");
      setLocalDescription(currentTP.description || "");
    }
  }, [visible, currentTP]);

  if (!visible || !currentTP) return null;

  const updateTouchpoints = (updater) => {
    if (isVideoMode) {
      const newScenes = content.scenes.map((scene, si) =>
        si === sceneIndex ? { ...scene, touchpoints: updater(scene.touchpoints) } : scene
      );
      onUpdate({ ...content, scenes: newScenes });
    } else {
      onUpdate({ ...content, touchpoints: updater(content.touchpoints) });
    }
  };

  const handleSave = () => {
    updateTouchpoints(tps => tps.map((tp, i) =>
      i === index ? { ...tp, title: localTitle, description: localDescription } : tp
    ));
    onClose();
  };

  const handleRemove = () => {
    updateTouchpoints(tps => tps.filter((_, i) => i !== index));
    onClose();
  };

  const handleClose = () => {
    if (!localTitle.trim() && !localDescription.trim()) {
      handleRemove();
    } else {
      onClose();
    }
  };
  const isMobile = window.innerWidth <= 768;
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="touchpoint-popover"
          style={getPopoverStyle(x, y, position)}
          {...(isMobile ? popoverPhoneAnimation : popoverAnimation)}
        >
          <HorizontalLayout style={{ justifyContent: "flex-end", alignItems: "top" }}>
            <p className="small-text semi-bold" style={{ flexGrow: 1 }}>{currentTP.title}</p>
            <CloseButton onClick={handleClose} />
          </HorizontalLayout>
          {readOnly ? (
            <VerticalLayout className="popover-content">
              <p className="xsmall-text">{currentTP.description}</p>
            </VerticalLayout>
          ) : (
            <VerticalLayout className="popover-editor">
              <InputForm
                ref={titleInputRef}
                title="Titel"
                value={localTitle}
                typeInput={setLocalTitle}
                placeholder="Voer titel in"
              />
              <TextAreaForm
                title="Beschrijving"
                value={localDescription}
                typeInput={setLocalDescription}
                placeholder="Voer beschrijving in"
              />
              <HorizontalLayout style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
                <StandardButton
                  onClick={handleSave}
                  title={<div className="center-btn"><IconDeviceFloppy width="1rem" height="1rem" /> Opslaan</div>}
                  backgroundColor="primary-600"
                  textColor="white"
                  style={{ marginLeft: "0px", flex: 1, textAlign: "center" }}
                />
                <StandardButton
                  onClick={handleRemove}
                  title={<><IconTrash width="1rem" height="1rem" /></>}
                  backgroundColor="error-100"
                  textColor="error-600"
                  style={{ marginLeft: "0px", minWidth: "auto", padding: "0.5rem" }}
                />
              </HorizontalLayout>
            </VerticalLayout>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HoverPreview({ visible, x, y }) {
  if (!visible) return null;

  const style = {
    position: "absolute",
    left: x - 32 / 2,
    top: y - 48 / 2,
    width: `32px`,
    height: `32px`,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "50%",
    pointerEvents: "none",
    transition: "opacity 0.1s ease-out",
    boxSizing: "border-box",
    border: "2px dashed rgba(255, 255, 255, 0.7)",
  };

  return (
    <motion.div
      style={style}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.15 }}
    >
    </motion.div>
  );
}

export default function InteractiveComp({ readOnly, content, onChange }) {
  let effectiveContent = content;
  let effectiveOnChange = onChange;
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isVideoMode = effectiveContent?.type === "video";

  if (!effectiveContent || Object.keys(effectiveContent).length === 0) {
    effectiveContent = { type: "image", imageUrl: null, touchpoints: [] };
    if (effectiveOnChange && !readOnly) {
      effectiveOnChange(effectiveContent);
    }
  }

  function selectImage(image) {
    setImageModalVisible(false);
    effectiveOnChange({
      type: "image",
      imageUrl: image.image.url,
      touchpoints: [],
    });
  }

  function selectImageMode() {
    effectiveOnChange({
      type: "image",
      imageUrl: null,
      touchpoints: [],
    });
    setImageModalVisible(true);
  }

  function selectVideoMode() {
    effectiveOnChange({
      type: "video",
      videoUrl: null,
      scenes: [],
    });
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 100);
  }

  async function handleVideoUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/quicktime",
      "video/x-m4v",
      "video/mov"
    ];
    if (!validTypes.includes(file.type)) {
      alert("Alleen MP4, WebM of OGG bestanden zijn toegestaan.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await postAPImedia("api/media/add-media", formData);
      effectiveOnChange({
        type: "video",
        videoUrl: `${ip}/api/media/${result.data.url}`,
        scenes: [],
      });
    } catch (error) {
      console.error("Upload mislukt", error);
      alert("Video uploaden is mislukt. Probeer opnieuw.");
    }
    setUploading(false);
  }

  const hasContent = (isVideoMode && effectiveContent?.videoUrl) || (!isVideoMode && effectiveContent?.imageUrl);

  if (!readOnly && !hasContent) {
    return (
      <>
        <div className="box-upload-image-btn">
          <IconPhoto size={64} color="#666" />
          <h5 style={{ margin: "1rem 0px 0.25rem 0px", color: "var(--grey-700)" }}>Touchpoint type kiezen</h5>
          <p style={{ margin: "0rem 0px 1.5rem 0px", textAlign: "center" }}>Kies of je touchpoints wilt plaatsen op een afbeelding of video.</p>
          <HorizontalLayout style={{ gap: "1rem", justifyContent: "center" }}>
            <StandardButton
              backgroundColor="primary-50"
              textColor="primary-600"
              onClick={selectImageMode}
              title={<><IconPhoto size={18} />&nbsp;&nbsp;Afbeelding</>}
            />
            <StandardButton
              backgroundColor="primary-600"
              textColor="white"
              onClick={selectVideoMode}
              title={<><IconVideo size={18} />&nbsp;&nbsp;Video</>}
              isDisabled={uploading}
            />
          </HorizontalLayout>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/ogg, video/quicktime,video/x-m4v,video/mov"
          style={{ display: "none" }}
          onChange={handleVideoUpload}
        />

        <ImageSelectorModalCopy
          visible={imageModalVisible}
          dismiss={() => setImageModalVisible(false)}
          imageSelected={selectImage}
          type={"a"}
        />
      </>
    );
  }

  if (isVideoMode) {
    return (
      <TouchPointsContainer>
        {readOnly ? (
          <VideoTouchpointPlayer
            content={effectiveContent}
          />
        ) : (
          <VideoTouchpointEditor
            content={effectiveContent}
            updateModuleContent={effectiveOnChange}
          />
        )}
      </TouchPointsContainer>
    );
  }

  return (
    <TouchPointsContainer>
      <TouchpointImage
        readOnly={readOnly}
        content={effectiveContent}
        updateModuleContent={effectiveOnChange}
      />
    </TouchPointsContainer>
  );
}

function TouchPointsContainer({ children }) {
  return (
    <div className="interactive-comp-container">
      {children}
    </div>
  );
}



const DEFAULT_FRAME_RATE = 30;

function timeToFrame(seconds, frameRate = DEFAULT_FRAME_RATE) {
  return Math.round(seconds * frameRate);
}

function frameToTime(frame, frameRate = DEFAULT_FRAME_RATE) {
  return frame / frameRate;
}

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatFrame(frame, frameRate = DEFAULT_FRAME_RATE) {
  return formatTime(frameToTime(frame, frameRate));
}

function getSceneFrame(scene) {
  return scene.frame !== undefined ? scene.frame : timeToFrame(scene.timestamp || 0);
}

function VideoTouchpointEditor({ content, updateModuleContent }) {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(0);
  const [editingSceneIndex, setEditingSceneIndex] = useState(null);
  const [activeTouchpointIndex, setActiveTouchpointIndex] = useState(null);
  const [newTouchpointIndex, setNewTouchpointIndex] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: -1, y: -1 });
  const [editingSceneTitle, setEditingSceneTitle] = useState(null);
  const [sceneTitleInput, setSceneTitleInput] = useState("");

  const scenes = content.scenes || [];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentFrame(timeToFrame(video.currentTime));
    const handleLoadedMetadata = () => setTotalFrames(timeToFrame(video.duration));
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setEditingSceneIndex(null);
      setActiveTouchpointIndex(null);
    } else {
      video.pause();
    }
  };

  const handleTimelineClick = (e) => {
    const video = videoRef.current;
    const timeline = timelineRef.current;
    if (!video || !timeline) return;

    const rect = timeline.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newFrame = Math.round(percentage * totalFrames);
    video.currentTime = frameToTime(newFrame);
    setCurrentFrame(newFrame);
  };

  const addScene = () => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    const newScene = {
      frame: currentFrame,
      title: `Scene ${scenes.length + 1}`,
      touchpoints: [],
    };
    const newScenes = [...scenes, newScene].sort((a, b) => a.frame - b.frame);
    updateModuleContent({ ...content, scenes: newScenes });

    const newIndex = newScenes.findIndex(s => s.frame === currentFrame);
    setEditingSceneIndex(newIndex);
  };

  const deleteScene = (index) => {
    const newScenes = scenes.filter((_, i) => i !== index);
    updateModuleContent({ ...content, scenes: newScenes });
    setEditingSceneIndex(null);
    setActiveTouchpointIndex(null);
  };

  const goToScene = (index) => {
    const video = videoRef.current;
    if (!video) return;

    const scene = scenes[index];
    const sceneFrame = getSceneFrame(scene);
    video.pause();
    video.currentTime = frameToTime(sceneFrame);
    setCurrentFrame(sceneFrame);
    setEditingSceneIndex(index);
    setActiveTouchpointIndex(null);
  };

  const startEditingSceneTitle = (index) => {
    setEditingSceneTitle(index);
    setSceneTitleInput(scenes[index].title);
  };

  const saveSceneTitle = (index) => {
    const newScenes = scenes.map((scene, i) =>
      i === index ? { ...scene, title: sceneTitleInput } : scene
    );
    updateModuleContent({ ...content, scenes: newScenes });
    setEditingSceneTitle(null);
  };

  const handleOverlayClick = (e) => {
    if (editingSceneIndex === null) return;

    if (activeTouchpointIndex !== null) {
      const currentScene = scenes[editingSceneIndex];
      const currentTP = currentScene?.touchpoints?.[activeTouchpointIndex];
      if (currentTP && !currentTP.title.trim() && !currentTP.description.trim()) {
        const newScenes = scenes.map((scene, si) => {
          if (si !== editingSceneIndex) return scene;
          const updated = scene.touchpoints.filter((_, ti) => ti !== activeTouchpointIndex);
          return { ...scene, touchpoints: updated };
        });
        updateModuleContent({ ...content, scenes: newScenes });
      }
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const xPercent = (clickX / rect.width) * 100;
    const yPercent = (clickY / rect.height) * 100;

    addTouchpoint(xPercent, yPercent);
  };

  const addTouchpoint = (x, y) => {
    const currentScene = scenes[editingSceneIndex];
    if (!currentScene) return;

    const newTouchpoint = {
      title: "",
      description: "",
      position: {
        x: `${x}%`,
        y: `${y}%`,
      },
    };

    const newTouchpoints = [...currentScene.touchpoints, newTouchpoint];
    const newScenes = scenes.map((scene, i) =>
      i === editingSceneIndex ? { ...scene, touchpoints: newTouchpoints } : scene
    );

    updateModuleContent({ ...content, scenes: newScenes });

    const indexOfNew = newTouchpoints.length - 1;
    setActiveTouchpointIndex(indexOfNew);
    setNewTouchpointIndex(indexOfNew);
  };

  const touchpointTouched = (index, e) => {
    e.stopPropagation();
    if (activeTouchpointIndex === index) {
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
    } else {
      setActiveTouchpointIndex(index);
      setNewTouchpointIndex(null);
    }
  };

  const handleMouseMove = (e) => {
    if (editingSceneIndex !== null) {
      const rect = e.currentTarget.getBoundingClientRect();
      setCursorPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseLeave = () => {
    setCursorPos({ x: -1, y: -1 });
  };

  const isHoverVisible = editingSceneIndex !== null && cursorPos.x >= 0;
  const currentScene = editingSceneIndex !== null ? scenes[editingSceneIndex] : null;

  return (
    <VerticalLayout style={{ width: "100%" }}>
      <div className="video-touchpoint-editor">
        <div
          className="video-container-wrapper"
          style={{ position: "relative" }}
        >
          <VideoElement videoRef={videoRef} src={content.videoUrl} onClick={togglePlayPause} />

          {editingSceneIndex !== null && (
            <div
              className="video-overlay"
              onClick={handleOverlayClick}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {isHoverVisible && (
                <HoverPreview visible={true} x={cursorPos.x} y={cursorPos.y} />
              )}

              <AnimatePresence>
                {(currentScene?.touchpoints || []).map((v, i) => (
                  <TouchpointMarker
                    key={i}
                    position={v.position}
                    isActive={activeTouchpointIndex === i}
                    onClick={(e) => touchpointTouched(i, e)}
                  >
                    <TouchpointPopover
                      visible={activeTouchpointIndex === i}
                      onClose={() => { setActiveTouchpointIndex(null); setNewTouchpointIndex(null); }}
                      x={"50%"}
                      y={"0%"}
                      readOnly={false}
                      onUpdate={updateModuleContent}
                      content={content}
                      index={i}
                      sceneIndex={editingSceneIndex}
                      isNewTouchpoint={newTouchpointIndex === i}
                      position={v.position}
                    />
                  </TouchpointMarker>
                ))}
              </AnimatePresence>
            </div>
          )}

          <TouchpointHintPill
            visible={editingSceneIndex !== null && currentScene?.touchpoints?.length === 0}
            text="Klik ergens in de video om een touchpoint aan te maken."
          />

          {editingSceneIndex !== null && (
            <SceneEditingIndicator title={currentScene?.title} onClose={() => setEditingSceneIndex(null)} />
          )}
        </div>

        <div className="video-controls">
          <VideoTimeline
            timelineRef={timelineRef}
            onClick={handleTimelineClick}
            progress={(currentFrame / totalFrames) * 100}
          >
            {scenes.map((scene, i) => (
              <SceneMarker
                key={i}
                active={editingSceneIndex === i}
                position={(getSceneFrame(scene) / totalFrames) * 100}
                onClick={(e) => { e.stopPropagation(); goToScene(i); }}
              />
            ))}
          </VideoTimeline>

          <HorizontalLayout style={{ gap: "0.5rem", marginTop: "0.75rem", justifyContent: "space-between" }}>
            <HorizontalLayout style={{ gap: "0.5rem", width: "auto" }}>
              <PlayPauseButton isPlaying={isPlaying} onClick={togglePlayPause} />
              <VideoTimeDisplay current={currentFrame} total={totalFrames} />
            </HorizontalLayout>
            <IconButton onClick={addScene} icon={<IconPlus size={20} />} title="Scene toevoegen op huidige positie" />
          </HorizontalLayout>
        </div>

        {scenes.length > 0 && (
          <div className="frame-manager">
            <p className="small-text semi-bold" style={{ marginBottom: "0.75rem" }}>Scenes ({scenes.length})</p>
            {scenes.map((scene, i) => (
              <SceneListItem
                key={i}
                scene={scene}
                isEditing={editingSceneIndex === i}
                isEditingTitle={editingSceneTitle === i}
                titleInput={sceneTitleInput}
                onTitleChange={(e) => setSceneTitleInput(e.target.value)}
                onTitleBlur={() => saveSceneTitle(i)}
                onTitleKeyDown={(e) => e.key === "Enter" && saveSceneTitle(i)}
                onTitleClick={() => startEditingSceneTitle(i)}
                onEdit={() => goToScene(i)}
                onDelete={() => deleteScene(i)}
              />
            ))}
          </div>
        )}
      </div>
    </VerticalLayout>
  );
}

function VideoTouchpointPlayer({ content }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(0);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(null);
  const [activeTouchpointIndex, setActiveTouchpointIndex] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [visitedScenes, setVisitedScenes] = useState(new Set());
  const lastFrameRef = useRef(0);

  const scenes = content.scenes || [];
  const sortedScenes = [...scenes].sort((a, b) => getSceneFrame(a) - getSceneFrame(b));

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const frame = timeToFrame(video.currentTime);
      const prevFrame = lastFrameRef.current;
      lastFrameRef.current = frame;
      setCurrentFrame(frame);

      if (currentSceneIndex !== null) return;

      for (let i = 0; i < sortedScenes.length; i++) {
        const scene = sortedScenes[i];
        const sceneFrame = getSceneFrame(scene);

        if (visitedScenes.has(i)) continue;

        const crossedScene = (prevFrame < sceneFrame && frame >= sceneFrame) ||
          (Math.abs(frame - sceneFrame) <= 2);

        if (crossedScene) {
          video.pause();
          video.currentTime = frameToTime(sceneFrame);
          setCurrentSceneIndex(i);
          setActiveTouchpointIndex(null);
          setVisitedScenes(prev => new Set([...prev, i]));
          break;
        }
      }
    };

    const handleLoadedMetadata = () => setTotalFrames(timeToFrame(video.duration));
    const handlePlay = () => {
      setIsPlaying(true);
      setHasStarted(true);
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentSceneIndex(null);
      setVisitedScenes(new Set());
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [currentSceneIndex, sortedScenes, visitedScenes]);

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play();
  };

  const handleContinue = () => {
    setCurrentSceneIndex(null);
    setActiveTouchpointIndex(null);
    const video = videoRef.current;
    if (video) {
      const nextFrame = currentFrame + 5;
      video.currentTime = frameToTime(nextFrame);
      lastFrameRef.current = nextFrame;
      video.play();
    }
  };

  const handleTimelineClick = (e) => {
    const video = videoRef.current;
    if (!video) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newFrame = Math.round(percentage * totalFrames);

    video.currentTime = frameToTime(newFrame);
    setCurrentFrame(newFrame);
    lastFrameRef.current = newFrame;
    setCurrentSceneIndex(null);

    const newVisited = new Set();
    for (let i = 0; i < sortedScenes.length; i++) {
      const scene = sortedScenes[i];
      const sceneFrame = getSceneFrame(scene);
      if (sceneFrame < newFrame) {
        newVisited.add(i);
      }
    }
    setVisitedScenes(newVisited);
  };

  const handleSceneMarkerClick = (index, e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    const scene = sortedScenes[index];
    const sceneFrame = getSceneFrame(scene);
    video.pause();
    video.currentTime = frameToTime(sceneFrame);
    setCurrentFrame(sceneFrame);
    lastFrameRef.current = sceneFrame;
    setCurrentSceneIndex(index);
    setActiveTouchpointIndex(null);
    setVisitedScenes(prev => new Set([...prev, index]));
  };

  const touchpointTouched = (index, e) => {
    e.stopPropagation();
    setActiveTouchpointIndex(activeTouchpointIndex === index ? null : index);
  };

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setCurrentSceneIndex(null);
      video.play();
    } else {
      video.pause();
    }
  };

  const currentScene = currentSceneIndex !== null ? sortedScenes[currentSceneIndex] : null;

  return (
    <VerticalLayout style={{ width: "100%" }}>
      <div className="video-touchpoint-player" ref={containerRef}>
        <div className="video-container-wrapper" style={{ position: "relative" }}>
          <VideoElement videoRef={videoRef} src={content.videoUrl} playsInline />

          {currentSceneIndex !== null && currentScene && (
            <div className="video-overlay video-overlay-readonly">
              <AnimatePresence>
                {(currentScene.touchpoints || []).map((v, i) => (
                  <TouchpointMarker
                    key={i}
                    position={v.position}
                    isActive={activeTouchpointIndex === i}
                    onClick={(e) => touchpointTouched(i, e)}
                  >
                    <TouchpointPopover
                      visible={activeTouchpointIndex === i}
                      onClose={() => setActiveTouchpointIndex(null)}
                      x={"50%"}
                      y={"0%"}
                      readOnly={true}
                      onUpdate={() => { }}
                      content={content}
                      index={i}
                      sceneIndex={currentSceneIndex}
                      isNewTouchpoint={false}
                      position={v.position}
                    />
                  </TouchpointMarker>
                ))}
              </AnimatePresence>
            </div>
          )}

          <AnimatePresence>
            {currentSceneIndex !== null && currentScene && (
              <SceneTitleDisplay title={currentScene.title} />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {currentSceneIndex !== null && (
              <ContinueButton onClick={handleContinue} />
            )}
          </AnimatePresence>

          {!hasStarted && <VideoPlayOverlay onClick={handlePlay} />}
        </div>

        <div className="video-controls video-controls-readonly">
          <VideoTimeline onClick={handleTimelineClick} progress={(currentFrame / totalFrames) * 100}>
            {sortedScenes.map((scene, i) => (
              <SceneMarker
                key={i}
                active={currentSceneIndex === i}
                position={(getSceneFrame(scene) / totalFrames) * 100}
                onClick={(e) => handleSceneMarkerClick(i, e)}
                title={scene.title}
              />
            ))}
          </VideoTimeline>

          <HorizontalLayout style={{ gap: "0.5rem", marginTop: "0.75rem", justifyContent: "space-between" }}>
            <HorizontalLayout style={{ gap: "0.5rem", width: "auto" }}>
              <PlayPauseButton isPlaying={isPlaying} onClick={togglePlayPause} />
              <VideoTimeDisplay current={currentFrame} total={totalFrames} />
            </HorizontalLayout>
          </HorizontalLayout>
        </div>
      </div>
    </VerticalLayout>
  );
}

function TouchpointImage({ readOnly, content, updateModuleContent }) {
  const [activeTouchpointIndex, setActiveTouchpointIndex] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: -1, y: -1 });
  const [newTouchpointIndex, setNewTouchpointIndex] = useState(null);

  const handleClick = (e, type) => {
    if (readOnly) return;
    if (type === "r") return;

    if (activeTouchpointIndex !== null) {
      const currentTP = content.touchpoints[activeTouchpointIndex];
      if (currentTP && !currentTP.title.trim() && !currentTP.description.trim()) {
        const updated = content.touchpoints.filter((tp, i) => i !== activeTouchpointIndex);
        updateModuleContent({ ...content, touchpoints: updated });
      }
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const offset = 16;
    const xPercent = ((clickX - offset) / rect.width) * 100;
    const yPercent = ((clickY - offset) / rect.height) * 100;

    addTouchpoint(xPercent, yPercent);
  };

  function addTouchpoint(x, y) {
    const allTouchpoints = [...(content.touchpoints || [])];
    const newTouchpoint = {
      title: "",
      description: "",
      position: {
        x: `${x}%`,
        y: `${y}%`,
      },
    };
    allTouchpoints.push(newTouchpoint);

    updateModuleContent({
      ...content,
      touchpoints: allTouchpoints,
    });

    const indexOfNew = allTouchpoints.length - 1;
    setActiveTouchpointIndex(indexOfNew);
    setNewTouchpointIndex(indexOfNew);
  }

  function touchpointTouched(index, e) {
    e.stopPropagation();
    if (activeTouchpointIndex === index) {
      setActiveTouchpointIndex(null);
      setNewTouchpointIndex(null);
    } else {
      setActiveTouchpointIndex(index);
      setNewTouchpointIndex(null);
    }
  }

  const handleMouseMove = (e) => {
    if (!readOnly) {
      const rect = e.currentTarget.getBoundingClientRect();
      setCursorPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }

  const handleMouseLeave = () => {
    setCursorPos({ x: -1, y: -1 });
  }

  const isHoverVisible = !readOnly && cursorPos.x >= 0;
  const isMobile = window.innerWidth <= 768;
  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <InteractiveImage
        src={content.imageUrl}
        onClick={(e) => handleClick(e, "l")}
        onContextMenu={(e) => { e.preventDefault(); handleClick(e, "r"); }}
      />

      <TouchpointHintPill
        visible={content.imageUrl && content.touchpoints.length === 0}
        text="Klik ergens in de afbeelding om een touchpoint aan te maken."
      />

      {isHoverVisible && <HoverPreview visible={true} x={cursorPos.x} y={cursorPos.y} />}

      <AnimatePresence>
        {(content.touchpoints || []).map((v, i) => (
          <ImageTouchpointWrapper key={i} position={v.position} isActive={activeTouchpointIndex === i}>
            <TouchpointMarker
              position={{ x: 0, y: 0 }}
              isActive={activeTouchpointIndex === i}
              onClick={(e) => touchpointTouched(i, e)}
            >
              <TouchpointPopover
                visible={activeTouchpointIndex === i}
                onClose={() => { setActiveTouchpointIndex(null); setNewTouchpointIndex(null); }}
                x={"50%"}
                y={"50%"}
                readOnly={readOnly}
                onUpdate={updateModuleContent}
                content={content}
                index={i}
                isNewTouchpoint={newTouchpointIndex === i}
                position={v.position}
              />
            </TouchpointMarker>
          </ImageTouchpointWrapper>
        ))}
      </AnimatePresence>
    </div>
  );
}
